import type { BoardDoc } from '../doc/BoardDoc';
import { DEG, type Point, type Rect } from '../doc/geometry';
import type { Item, StrokeItem } from '../doc/schema';
import type { Tool } from './paneStore';

/**
 * Ink: freehand strokes. They live on automatic ink layers (one per pen tool) and are only
 * selectable while the draw tool is active, through the lasso, so sketching never clutters the
 * ordinary select tool. This module holds the selection rule and the stroke geometry that the
 * lasso, the tap-select and the eraser share.
 */

export const isInk = (item: Item): item is StrokeItem => item.type === 'stroke';

/** Can this tool put the item into the selection? Ink belongs to the draw tool, everything else to the rest. */
export function canSelect(item: Item, tool: Tool): boolean {
  return isInk(item) === (tool === 'draw');
}

/** Keeps only the ids the tool may select. */
export function filterSelectable(board: BoardDoc, ids: Iterable<string>, tool: Tool): string[] {
  const out: string[] = [];
  for (const id of ids) {
    const item = board.index.get(id);
    if (item && canSelect(item, tool)) out.push(id);
  }
  return out;
}

/**
 * The stroke's centre-line as flat world coordinates [x, y, x, y, ...], with the item's scale
 * (box vs. drawn size) and rotation applied.
 */
export function strokeWorldPoints(item: StrokeItem): number[] {
  const { points, w0, h0 } = item.props;
  const sx = item.w / Math.max(1, w0);
  const sy = item.h / Math.max(1, h0);
  const cx = item.x + item.w / 2;
  const cy = item.y + item.h / 2;
  const rad = (item.rotation || 0) * DEG;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const out: number[] = [];
  for (let i = 0; i + 1 < points.length; i += 3) {
    const lx = points[i]! * sx - item.w / 2;
    const ly = points[i + 1]! * sy - item.h / 2;
    out.push(cx + lx * cos - ly * sin, cy + lx * sin + ly * cos);
  }
  return out;
}

/** Squared distance from p to the segment ab. */
function segDist2(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const qx = ax + t * dx - px;
  const qy = ay + t * dy - py;
  return qx * qx + qy * qy;
}

/** Is the world point within `radius` of the stroke's centre-line (plus half its brush size)? */
export function strokeHit(item: StrokeItem, world: Point, radius: number): boolean {
  const pts = strokeWorldPoints(item);
  const r = radius + item.props.size / 2;
  const r2 = r * r;
  if (pts.length === 2) return segDist2(world.x, world.y, pts[0]!, pts[1]!, pts[0]!, pts[1]!) <= r2;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    if (segDist2(world.x, world.y, pts[i]!, pts[i + 1]!, pts[i + 2]!, pts[i + 3]!) <= r2) return true;
  }
  return false;
}

/** Topmost selectable stroke under a world point (eraser and lasso tap). */
export function strokeAt(board: BoardDoc, world: Point, radius: number): StrokeItem | null {
  const hits = board.index.query({ x: world.x - radius, y: world.y - radius, w: radius * 2, h: radius * 2 });
  for (let i = hits.length - 1; i >= 0; i--) {
    const item = hits[i]!;
    if (isInk(item) && !board.isHidden(item) && !board.isLocked(item) && strokeHit(item, world, radius)) return item;
  }
  return null;
}

// ---- lasso ------------------------------------------------------------------

/** Even-odd point-in-polygon on a flat [x, y, ...] ring. */
function insidePolygon(px: number, py: number, ring: number[]): boolean {
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 2; i < n; j = i, i += 2) {
    const xi = ring[i]!;
    const yi = ring[i + 1]!;
    const xj = ring[j]!;
    const yj = ring[j + 1]!;
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

const cross = (ax: number, ay: number, bx: number, by: number) => ax * by - ay * bx;

/** Do the segments p1-p2 and p3-p4 intersect (including touching)? */
function segmentsIntersect(
  x1: number, y1: number, x2: number, y2: number,
  x3: number, y3: number, x4: number, y4: number,
): boolean {
  const d1 = cross(x4 - x3, y4 - y3, x1 - x3, y1 - y3);
  const d2 = cross(x4 - x3, y4 - y3, x2 - x3, y2 - y3);
  const d3 = cross(x2 - x1, y2 - y1, x3 - x1, y3 - y1);
  const d4 = cross(x2 - x1, y2 - y1, x4 - x1, y4 - y1);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;
  const on = (ax: number, ay: number, bx: number, by: number, px: number, py: number) =>
    Math.min(ax, bx) <= px && px <= Math.max(ax, bx) && Math.min(ay, by) <= py && py <= Math.max(ay, by);
  if (d1 === 0 && on(x3, y3, x4, y4, x1, y1)) return true;
  if (d2 === 0 && on(x3, y3, x4, y4, x2, y2)) return true;
  if (d3 === 0 && on(x1, y1, x2, y2, x3, y3)) return true;
  if (d4 === 0 && on(x1, y1, x2, y2, x4, y4)) return true;
  return false;
}

/** Does the stroke's centre-line lie inside the ring, or cross its edge? */
export function strokeInLasso(item: StrokeItem, ring: number[]): boolean {
  const pts = strokeWorldPoints(item);
  if (pts.length < 2 || ring.length < 6) return false;
  for (let i = 0; i < pts.length; i += 2) {
    if (insidePolygon(pts[i]!, pts[i + 1]!, ring)) return true;
  }
  if (pts.length === 2) {
    // a dot: near enough to the lasso's edge counts as touched
    const r2 = (item.props.size / 2) ** 2;
    for (let j = 0, k = ring.length - 2; j < ring.length; k = j, j += 2) {
      if (segDist2(pts[0]!, pts[1]!, ring[j]!, ring[j + 1]!, ring[k]!, ring[k + 1]!) <= r2) return true;
    }
    return false;
  }
  for (let i = 0; i + 3 < pts.length; i += 2) {
    for (let j = 0, k = ring.length - 2; j < ring.length; k = j, j += 2) {
      if (segmentsIntersect(pts[i]!, pts[i + 1]!, pts[i + 2]!, pts[i + 3]!, ring[j]!, ring[j + 1]!, ring[k]!, ring[k + 1]!)) return true;
    }
  }
  return false;
}

export function ringBounds(ring: number[]): Rect | null {
  if (ring.length < 2) return null;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (let i = 0; i < ring.length; i += 2) {
    const x = ring[i]!;
    const y = ring[i + 1]!;
    if (x < x0) x0 = x;
    if (x > x1) x1 = x;
    if (y < y0) y0 = y;
    if (y > y1) y1 = y;
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** Ids of the visible, unlocked strokes the lasso ring (flat world coords) touches or encloses. */
export function lassoStrokes(board: BoardDoc, ring: number[]): string[] {
  const bounds = ringBounds(ring);
  if (!bounds || ring.length < 6) return [];
  const out: string[] = [];
  for (const item of board.index.query(bounds)) {
    if (isInk(item) && !board.isHidden(item) && !board.isLocked(item) && strokeInLasso(item, ring)) out.push(item.id);
  }
  return out;
}
